import { React, useState, useMemo, useEffect, useRef, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
// Компоненты пользовательского интерфейса
import '../../styles/EditDevice.css';
import { editDeviceFromServer, initSimInfoFromServer } from '../../redux/actions/devices.action';
import CustomButton from '../CustomElements/CustomButton';
import CustomTitle from '../CustomElements/CustomTitle';
import CustomContainer from '../CustomElements/CustomContainer';
import FormContainer from '../CustomElements/FormContainer';
import Selector from '../CustomElements/Selector';
import DateSelector from '../CustomElements/DateSelector';
import PhoneInput from '../CustomElements/PhoneInput';
import ConfirmationPage from '../CustomElements/ConfirmationPage';
// Кастомные хуки
import { useButtonValidation } from '../../customHook/useButtonValidation';
import useFormDataChanged from '../../customHook/useFormDataChanged';
// Утилиты
import { handleUniversalChange } from '../../utils/formUtils';
import { omitFields } from '../../utils/omitFields';
import { convertToDate, transformFieldsToMoscowStart } from '../../utils/convertToDate';
import { prepareObjectForSubmit } from '../../utils/prepareObjectForSubmit';
import { validateSimNumbers } from '../../utils/validateSimNumbers';
// Действия Redux
import { addError } from '../../redux/actions/error.action';
import { load, remove } from '../../redux/actions/loader.action';
import { initAndroidIdFromServer } from '../../redux/actions/devices.action';
import Input from '../CustomElements/Input';

// Этот компонент отображает форму для изменения данных устройства

function EditDevice({ closeModalDevice, currentDevice, typeUipps, t }) {
  // Получение функции dispatch для отправки действий в Redux store.
  const dispatch = useDispatch();

  // проверяем что тип устройства ИУР или УИППС */
  const isIurOrUipps = [1, 4].includes(currentDevice?.typeId);

  // Используем useRef для отслеживания первого рендера
  const isFirst = useRef(true);

  // Получаем опции
  const optionsForSelect = useSelector((state) => state.options);

  // Получаем данные о процессе
  const loader = useSelector((state) => state.loader);

  // Получение сообщения об ошибке из глобального состояния
  const error = useSelector((state) => state.error);

  // Состояния для кнопки и уведомления об успешном добавлении
  const [editSuccess, setEditSuccess] = useState(false);

  // Состояние для формы
  const initialFormData = useMemo(
    () => ({
      deviceId: currentDevice?.id,
      sim1Number: currentDevice?.sim1Number || '',
      sim1Operator: currentDevice?.sim1Operator || '',
      sim1Serial: currentDevice?.sim1Serial || '',
      manufactureDate: convertToDate(currentDevice.manufactureDate) || '',
      sim2Number: currentDevice?.sim2Number || '',
      sim2Operator: currentDevice?.sim2Operator || '',
      sim2Serial: currentDevice?.sim2Serial || '',
      androidId: currentDevice?.androidId || '',
      serial: currentDevice?.serial || '',
    }),
    [currentDevice]
  );

  const [formData, setFormData] = useState(initialFormData);

  //проверяем были ли изменения в форме
  const isFormEdit = useFormDataChanged(initialFormData, formData, true);

  // Вычисляем optionalFields на основе formData.deviceId
  const optionalFields = useMemo(() => {
    if (formData.deviceId >= 300000 && formData.deviceId <= 399999) {
      return [
        'sim1Number',
        'sim2Number',
        'sim1Operator',
        'sim2Operator',
        'sim1Serial',
        'sim2Serial',
        'androidId',
        'serial',
      ];
    }
    return ['sim1Number', 'sim2Number', 'sim1Operator', 'sim2Operator', 'sim1Serial', 'sim2Serial', 'serial'];
  }, [formData.deviceId]);

  //Функция для проверки валидности формы
  const isButtonDisabled = useButtonValidation(formData, optionalFields);

  // Обработчик отправки формы
  const handlerSubmit = (event) => {
    event.preventDefault();
    dispatch(load());
    // сравниваем начальное и текущее состояние заменяем на null пустые строки для удаленных полей
    const formDataWithEmptyStrings = prepareObjectForSubmit(initialFormData, formData);
    // Удаляем поля, которые не нужно отправлять
    const formWithoutExcluded = omitFields(formDataWithEmptyStrings, ['deviceId']);
    // Преобразуем указанные поля в начало дня по Москве (если значение есть и валидное)
    const payload = transformFieldsToMoscowStart(formWithoutExcluded, ['manufactureDate']);
    dispatch(editDeviceFromServer(payload, formData.deviceId, typeUipps));
  };

  // Закрывает модальное окно после успешного изменения устройства
  const handlerCloseModal = () => {
    closeModalDevice();
    setEditSuccess(false);
  };

  // Функция для обработки клика по кнопке открывает модальное окно подтверждения
  // и проверяет валидность SIM-карт
  const handleButtonClick = (event) => {
    event.preventDefault();
    const checkSim = validateSimNumbers(formData, setFormData);
    if (checkSim) {
      dispatch(addError({ error: checkSim }));
    } else if (!checkSim) {
      setEditSuccess(!editSuccess);
    }
  };

  // функция для запроса обновленных данных SIM-карт по androidId и записи в форму
  const applySimInfoByAndroidId = useCallback(
    async (androidId) => {
      const simInfo = await dispatch(initSimInfoFromServer({ androidId, typeId: currentDevice?.typeId }));
      if (simInfo) {
        setFormData((prev) => ({
          ...prev,
          ...Object.fromEntries(Object.entries(simInfo).map(([key, value]) => [key, value ?? ''])),
        }));
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [dispatch]
  );

  // Отслеживаем изменения в formData.androidId
  useEffect(() => {
    if (isFirst.current) {
      isFirst.current = false;
      return;
    }
    // Если androidId изменился, отправляем запрос на получение информации по этому androidId
    if (formData.androidId) {
      applySimInfoByAndroidId(formData?.androidId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData?.androidId]);

  //отслеживаем выполнение запроса
  useEffect(() => {
    // Если нет ошибок, закрываем модальное окно
    if (!loader?.isLoading && loader?.isSuccess) {
      //Меняем флаг для отображения сообщения что устройство изменено
      handlerCloseModal();
    }
    // при закрытии компонента чистим стейт загрузки
    return () => {
      dispatch(remove());
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loader?.isLoading, loader?.isSuccess, error]);

  // отправляем запрос на получений списка androidId при открытии формы
  useEffect(() => {
    dispatch(initAndroidIdFromServer({ typeId: currentDevice?.typeId }));
  }, [dispatch, currentDevice?.typeId]);

  return (
    <CustomContainer height="100%">
      <CustomTitle
        titleText={t?.editDeviceTitle || 'Изменение данных'}
        deviceType={currentDevice.type}
        deviceId={currentDevice.id}
      />
      {editSuccess && (
        <ConfirmationPage
          confirmText={t?.confirmEditDeviceText || 'Вы уверены, что хотите изменить данные устройства?'}
          confirmButtonText={t?.confirmButton || 'Изменить'}
          cancelButtonText={t?.cancelButton || 'Отменить'}
          onConfirm={handlerSubmit} // Функция, которая должна быть вызвана при подтверждении
          onCancel={handlerCloseModal} // Функция для закрытия модального окна или отмены действия
        />
      )}
      {!editSuccess && (
        <>
          <FormContainer>
            <PhoneInput
              column="left"
              label={t?.labelSim1 || 'SIM1'}
              name="sim1Number"
              onChange={(event) => handleUniversalChange(formData, setFormData, event)}
              value={formData?.sim1Number}
            />
            {isIurOrUipps && (
              <Input
                column="left"
                value={formData?.sim1Operator}
                label={t?.labelOperator1 || 'Оператор SIM1'}
                name="sim1Operator"
                width="100%"
                height="var(--sadr-font-size-60)"
                isDisabled={true}
              />
            )}
            {isIurOrUipps && (
              <Input
                column="left"
                value={formData?.sim1Serial}
                label="ICCID SIM1"
                name="sim1Serial"
                width="100%"
                height="var(--sadr-font-size-60)"
                isDisabled={true}
              />
            )}
            {isIurOrUipps && (
              <Input
                column="left"
                value={formData?.serial}
                label={t?.serialNumber || 'Серийный номер'}
                name="serial"
                width="100%"
                height="var(--sadr-font-size-60)"
                isDisabled={true}
              />
            )}
            <DateSelector
              column="left"
              width="100%"
              height="var(--sadr-font-size-60)"
              label={t?.labelManufactureDate || 'Дата производства *'}
              selectedDate={formData.manufactureDate}
              setSelectedDate={(date) =>
                handleUniversalChange(formData, setFormData, null, 'manufactureDate', date, 'date')
              }
              name="manufactureDate"
              placeholder={t?.placeholderManufactureDate || 'Выберите дату производства'}
              maxDate={new Date()}
            />
            <PhoneInput
              column="right"
              label={t?.labelSim2 || 'SIM2'}
              name="sim2Number"
              onChange={(event) => handleUniversalChange(formData, setFormData, event)}
              value={formData.sim2Number}
            />
            {isIurOrUipps && (
              <Input
                column="right"
                value={formData?.sim2Operator}
                label={t?.labelOperator2 || 'Оператор SIM2'}
                name="sim2Operator"
                width="100%"
                height="var(--sadr-font-size-60)"
                isDisabled={true}
              />
            )}
            {isIurOrUipps && (
              <Input
                column="right"
                value={formData?.sim2Serial}
                label="ICCID SIM1"
                name="sim2Serial"
                width="100%"
                height="var(--sadr-font-size-60)"
                isDisabled={true}
              />
            )}
            {isIurOrUipps && (
              <Selector
                column="right"
                options={typeUipps ? optionsForSelect?.uippsAndroidId : optionsForSelect?.iurAndroidId}
                value={
                  optionsForSelect?.[typeUipps ? 'uippsAndroidId' : 'iurAndroidId']?.find(
                    (option) => option.value === formData.androidId
                  ) || { value: formData.androidId, label: formData.androidId } ||
                  ''
                }
                onChange={(selectedOption) =>
                  handleUniversalChange(formData, setFormData, null, 'androidId', selectedOption, 'select')
                }
                placeholder={t?.placeholderSelectAndroidId || 'Выберите android_id'}
                label={t?.labelAndroidId || 'ANDROID_ID *'}
                width="100%"
                height="var(--sadr-font-size-60)"
                isMulti={false}
                name="androidId"
                optionText={
                  typeUipps
                    ? t?.textNoUnauthorizedUipps || 'Нет неавторизованных УИППС'
                    : t?.textNoUnauthorizedIur || 'Нет неавторизованных ИУР'
                }
              />
            )}
          </FormContainer>
          <CustomButton
            width="var(--sadr-font-size-420)"
            height="var(--sadr-font-size-60)"
            text={t?.buttonSaveChanges || 'Сохранить изменения'}
            disabled={isButtonDisabled || !isFormEdit}
            onClick={handleButtonClick}
            isButton
          />
        </>
      )}
    </CustomContainer>
  );
}
export default EditDevice;
